import { Component, Input, OnChanges } from '@angular/core';
import { Store } from '@ngneat/elf';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { LinearInterpSingleVal } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { UndisturbedTemperaturePlot } from 'src/app/wellbore-inputs/models/undisturbed-temperature.model';

@Component({
    selector: 'app-post-install-loads-plot',
    template: `
    <app-tool-loader [isLoading]="isLoadingPlot" [positionUnset]="true">
      @if (tablePlotSelected === 'plot') {
        <app-xy-line-plot
          [plotData]="plot.data"
          [xAxisTitle]="xAxisTitle"
          [yAxisTitle]="yAxisTitle"
          [plotName]="plotName"
          [downloadPlotName]="downloadPlotName"
          [plotTitle]="'Post-Install Load Cases'"
          [xyLinePlotStore]="postInstallLoadsStore"
        ></app-xy-line-plot>
      }

      @if (tablePlotSelected === 'grid') {
        <app-single-load-grid
          [rowData]="gridRowData"
          [columnDefinitions]="gridColumnDefinitions"
          tableName="Post-Install Load Cases"
          gridName="Post-Install Load Cases"
        ></app-single-load-grid>
      }
    </app-tool-loader>
  `,
    styles: [':host { display: block; height: 100%; min-height: 0; }'],
    standalone: false
})
export class PostInstallLoadsPlotComponent implements OnChanges {
  @Input() plotData: any;
  @Input() udt!: UndisturbedTemperaturePlot;
  @Input() pressTempSelected!: string;
  @Input() tablePlotSelected: string = 'plot';
  @Input() hangerTvd!: number;
  @Input() shoeDepthTvd!: number;
  @Input() userUnits!: UserUnitsModel;
  @Input() stringName!: string;
  @Input() postInstallLoadsStore!: Store;
  @Input() plotName!: string;
  @Input() isLoadingPlot!: boolean;

  public plot = {
    data: [] as any[],
    layout: {},
    config: {}
  };

  public gridRowData: any[] = [];
  public gridColumnDefinitions: { header: string; field: string; valueFormatter(params: any): string }[] = [];

  public xAxisTitle = '';
  public yAxisTitle = '';
  public downloadPlotName = 'load_case_plot';

  ngOnChanges() {
    if (this.plotData) {
      this.plotPressures(this.plotData, this.udt, this.pressTempSelected);
      this.buildGridData(this.plotData, this.pressTempSelected);
    }
  }

  async plotPressures(plotData: any, udt: any, pressTempSelected: string) {
    this.yAxisTitle = `TVD (${this.userUnits?.longLengths})`;
    this.xAxisTitle = pressTempSelected === 'temp' ? `Temperature (${this.userUnits?.temperature})` : `Pressure (${this.userUnits?.pressure})`;

    this.plot.data = [];

    if (!plotData || plotData === null) {
      // No data
      const defaultPlot = {
        x: 0,
        y: 0
      }

      this.plot.data.push(defaultPlot);
    } else {
      // IPP
      const intPress = {
        name: "Internal Pressure Profile",
        mode: 'lines',
        x: plotData.internalPressurePoints?.map((point: any) => Math.trunc(point.pressure * 100) / 100),
        y: plotData.internalPressurePoints?.map((point: any) => Math.trunc(point.trueVerticalDepth * 100) / 100)
      }

      // EPP
      const extPress = {
        name: "External Pressure Profile",
        mode: 'lines',
        x: plotData.externalPressurePoints?.map((point: any) => Math.trunc(point.pressure * 100) / 100),
        y: plotData.externalPressurePoints?.map((point: any) => Math.trunc(point.trueVerticalDepth * 100) / 100)
      }

      // Temp
      const temp = {
        name: 'Temperature',
        mode: 'lines',
        x: plotData.temperaturePoints?.map((point: any) => Math.trunc(point.temperature * 100) / 100),
        y: plotData.temperaturePoints?.map((point: any) => Math.trunc(point.trueVerticalDepth * 100) / 100),
        line: {
          color: '#FF0000'
        }
      }

      if (pressTempSelected === 'press') {
        this.plot.data.push(intPress);
        this.plot.data.push(extPress);
      } else {
        this.plot.data.push(temp);
      }

      // UDT
      if (udt && pressTempSelected === 'temp') {
        const deepestPoint = Math.max(...temp.y, this.shoeDepthTvd);

        const interpolatedX = LinearInterpSingleVal(deepestPoint, udt.data.y, udt.data.x);
        udt.data.x.push(interpolatedX);
        udt.data.y.push(deepestPoint);

        const deepestShoeIndex = udt.data.y.findIndex((depth: number) => depth > deepestPoint);
        udt.data.y.splice(deepestShoeIndex, 1);
        udt.data.x.splice(deepestShoeIndex, 1);

        const udtPlot = {
          name: 'UDT',
          x: udt.data.x,
          y: udt.data.y,
          mode: 'lines',
          line: {
            dash: 'dot',
            color: '#FF0000'
          }
        }

        this.plot.data.push(udtPlot);
      }

      // Casing
      const largestPointInt = Math.max(...extPress.x);
      const largestPointExt = Math.max(...intPress.x);
      const largestUdt = udt?.data.x ? Math.max(...udt.data.x) : 0;

      let largestValue = Math.max(largestPointExt, largestPointInt) + 10;

      if (pressTempSelected === 'temp') {
        largestValue = Math.max(...temp.x, largestUdt) + 10;
      }

      const casing = {
        name: this.stringName,
        x: [largestValue, largestValue],
        y: [this.hangerTvd, this.shoeDepthTvd],
        marker: {
          size: 10,
          symbol: ['line-ew', "triangle-up-dot", "line-ew", "line-ew", "triangle-up-dot", "line-ew"]
        },
        line: {
          color: '#999999'
        },
        hovertemplate: `TVD: %{y} ${this.userUnits.longLengths}`
      }

      this.plot.data.push(casing);
    }
  }

  private buildGridData(plotData: any, pressTempSelected: string): void {
    if (pressTempSelected === 'press') {
      this.gridColumnDefinitions = [
        { header: `Internal TVD (${this.userUnits?.longLengths})`, field: 'internalTvd', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
        { header: `Internal Pressure (${this.userUnits?.pressure})`, field: 'internalPressure', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
        { header: `External TVD (${this.userUnits?.longLengths})`, field: 'externalTvd', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
        { header: `External Pressure (${this.userUnits?.pressure})`, field: 'externalPressure', valueFormatter: (params: any) => this.formatDecimal(params, 100) }
      ];

      const rowCount = Math.max(plotData?.internalPressurePoints?.length ?? 0, plotData?.externalPressurePoints?.length ?? 0);
      this.gridRowData = Array.from({ length: rowCount }, (_, index) => ({
        internalTvd: plotData?.internalPressurePoints?.[index]?.trueVerticalDepth,
        internalPressure: plotData?.internalPressurePoints?.[index]?.pressure,
        externalTvd: plotData?.externalPressurePoints?.[index]?.trueVerticalDepth,
        externalPressure: plotData?.externalPressurePoints?.[index]?.pressure
      }));
      return;
    }

    this.gridColumnDefinitions = [
      { header: `TVD (${this.userUnits?.longLengths})`, field: 'trueVerticalDepth', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Temperature (${this.userUnits?.temperature})`, field: 'temperature', valueFormatter: (params: any) => this.formatDecimal(params, 100) }
    ];

    this.gridRowData = (plotData?.temperaturePoints ?? []).map((point: any) => ({
      trueVerticalDepth: point.trueVerticalDepth,
      temperature: point.temperature
    }));
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : '';
  }
}
